import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock, ArrowLeft, Home, FolderOpen, Maximize2, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Projectos = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [openedProject, setOpenedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/projects`);
        setProjects(res.data || []);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load projects', e);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === '6380') {
      setIsAuthenticated(true);
      setError('');
      setPassword('');
    } else {
      setError('Código incorreto. Tente novamente.');
      setPassword('');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />

        <Card className="w-full max-w-md bg-zinc-900/80 border-gold/30 backdrop-blur-xl relative z-10">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mb-4">
              <Lock className="h-7 w-7 text-gold" />
            </div>
            <CardTitle className="text-3xl text-white heading-luxury">
              <span className="text-gold">Projectos</span>
            </CardTitle>
            <p className="text-gray-400 mt-2 text-sm tracking-wide uppercase">
              Acesso Restrito
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-5 mt-4">
              <div>
                <Label htmlFor="projectos-code" className="text-white">
                  Código
                </Label>
                <Input
                  id="projectos-code"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite o código"
                  className="mt-2 bg-black/60 border-gold/30 text-white placeholder:text-gray-500 focus:border-gold"
                  data-testid="projectos-code-input"
                  autoFocus
                />
                {error && (
                  <p
                    className="text-red-400 text-sm mt-2"
                    data-testid="projectos-code-error"
                  >
                    {error}
                  </p>
                )}
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-gold to-gold-light hover:from-gold-light hover:to-gold text-black font-semibold py-6"
                data-testid="projectos-login-btn"
              >
                Aceder
              </Button>
              <a
                href="/"
                className="block text-center text-sm text-gray-400 hover:text-gold transition-colors"
                data-testid="projectos-back-home"
              >
                <ArrowLeft className="inline h-4 w-4 mr-1" />
                Voltar à página inicial
              </a>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Project viewer (iframe full-screen)
  if (openedProject) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="projectos-viewer">
        <div className="flex items-center justify-between px-4 py-2 bg-black/95 border-b border-gold/20">
          <button
            onClick={() => setOpenedProject(null)}
            className="flex items-center gap-2 text-sm text-gray-300 hover:text-gold transition-colors"
            data-testid="projectos-viewer-close"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar à lista
          </button>
          <div className="text-xs text-gray-500 uppercase tracking-wider hidden md:block">
            {openedProject.title} · {openedProject.subtitle}
          </div>
          <a
            href={openedProject.href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-gray-300 hover:text-gold transition-colors"
            data-testid="projectos-viewer-fullscreen"
          >
            <Maximize2 className="h-4 w-4" />
            <span className="hidden sm:inline">Abrir em nova aba</span>
          </a>
        </div>
        <iframe
          src={openedProject.href}
          title={openedProject.title}
          className="flex-1 w-full border-0 bg-black"
          data-testid="projectos-iframe"
        />
      </div>
    );
  }

  // Project list
  return (
    <div className="min-h-screen bg-black text-white" data-testid="projectos-list">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-black/95 backdrop-blur-md border-b border-gold/20">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <a
            href="/"
            className="flex items-center gap-2 text-gray-300 hover:text-gold transition-colors text-sm"
            data-testid="projectos-nav-home"
          >
            <Home className="h-4 w-4" />
            Início
          </a>
          <span className="text-xs text-gray-500 uppercase tracking-wider">
            Dossiers Confidenciais
          </span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden border-b border-gold/10">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-zinc-950 to-black" />
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
            <FolderOpen className="h-4 w-4 text-gold" />
            <span className="text-gold text-xs font-medium tracking-[0.3em] uppercase">
              Apex Team · Projectos
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold heading-luxury mb-4">
            <span className="text-white">Dossiers </span>
            <span className="text-gold">curados.</span>
          </h1>

          <div className="h-1 w-24 gold-gradient mx-auto mb-6" />

          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed font-light">
            Selecção de propostas e estudos preparados pela Apex Team.
          </p>
        </div>
      </section>

      {/* Project grid */}
      <section className="py-16 md:py-24 max-w-6xl mx-auto px-4">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 text-gold animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <p className="text-center text-gray-500 py-20">
            Sem dossiers disponíveis no momento.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((p) => (
            <button
              key={p.slug}
              onClick={() => setOpenedProject(p)}
              className="group text-left rounded-2xl overflow-hidden border border-gold/20 hover:border-gold/60 transition-all duration-500 bg-zinc-900/40 luxury-shadow"
              data-testid={`projectos-card-${p.slug}`}
            >
              <div className="aspect-[4/5] overflow-hidden bg-zinc-900">
                <img
                  src={p.cover}
                  alt={p.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-6 space-y-3">
                <div className="text-gold text-[10px] tracking-[0.3em] uppercase">
                  {p.subtitle}
                </div>
                <h3 className="text-2xl font-bold text-white heading-luxury">
                  {p.title}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed line-clamp-3">
                  {p.description}
                </p>
                <div className="pt-2 flex items-center gap-2 text-gold text-sm font-medium">
                  Abrir dossier
                  <ExternalLink className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </div>
            </button>
          ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Projectos;
